import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { logIn, useUserAuth } from '../../UserAuth';
import { db } from '../../firebase';
import { BIBLE_LANGUAGES } from '../../constants';
import { dedupeVerseTuples, isValidVerseTuple, verseTupleToKey } from '../../utils/canonical_verse_utils';
import {
  createPace,
  DECK_PACE_TIERS,
  getPaceTier,
  getTodayDateString,
  isValidDueDate,
  recommendDeckPace,
} from '../../utils/deck_pace_utils';
import { createDeckInDatabase } from '../../utils/deck_service';
import { fetchLSMVersesWithRetry } from '../../utils/lsm_api';
import { getBibleLanguageFromCookie } from '../../utils/theme_utils';
import {
  formatVerseTuple,
  parseVerseReferences,
} from '../../utils/verse_reference_parser';
import { useViewportHeight } from '../../utils/viewportUtils';
import { Icon } from '../Icon';
import { MaterialHeader } from '../MaterialHeader';
import { VersePreviewList } from './VersePreviewList';

const DRAFT_KEY = 'createDeckDraft';
const PAGE_SIZE = 50;
const PREVIEW_SIZE = 5;

const fieldStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px',
  border: '1px solid var(--md-sys-color-outline)',
  borderRadius: '12px',
  backgroundColor: 'var(--md-sys-color-surface-container-lowest)',
  color: 'var(--md-sys-color-on-surface)',
  font: 'inherit',
};

const primaryButtonStyle = {
  minHeight: '44px',
  padding: '10px 20px',
  border: 'none',
  borderRadius: '22px',
  backgroundColor: 'var(--md-sys-color-primary)',
  color: 'var(--md-sys-color-on-primary)',
  font: 'inherit',
  fontWeight: 500,
  cursor: 'pointer',
};

function loadDraft() {
  try {
    const stored = localStorage.getItem(DRAFT_KEY);
    if (!stored) {
      return null;
    }
    const draft = JSON.parse(stored);
    const selectedTier = draft.tierId === 'custom'
      || DECK_PACE_TIERS.some(tier => tier.id === draft.tierId)
      ? draft.tierId
      : 'medium';
    const tier = DECK_PACE_TIERS.find(option => option.id === selectedTier);
    const targetIsValid = Number.isInteger(draft.paceTarget)
      && draft.paceTarget > 0
      && (
        selectedTier === 'custom'
        || (draft.paceTarget >= tier.min_target && draft.paceTarget <= tier.max_target)
      );
    const selectedTarget = targetIsValid ? draft.paceTarget : (tier?.default_target || 3);
    return {
      currentStep: [1, 2, 3].includes(draft.currentStep) ? draft.currentStep : 1,
      selectedVerses: Array.isArray(draft.selectedVerses)
        ? draft.selectedVerses.filter(isValidVerseTuple)
        : [],
      tierId: selectedTier,
      paceTarget: selectedTarget,
      dueDate: typeof draft.dueDate === 'string' ? draft.dueDate : '',
      deckName: typeof draft.deckName === 'string' ? draft.deckName : '',
      isPublic: draft.isPublic === true,
    };
  } catch (error) {
    console.warn('Unable to load deck draft:', error);
    return null;
  }
}

function saveDraft(draft) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch (error) {
    console.warn('Unable to save deck draft:', error);
  }
}

function clearDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch (error) {
    console.warn('Unable to clear deck draft:', error);
  }
}

export function addVerseReferenceInput(currentVerses, input) {
  return dedupeVerseTuples([...currentVerses, ...parseVerseReferences(input)]);
}

export function removeSelectedVerse(currentVerses, tupleToRemove) {
  const keyToRemove = verseTupleToKey(tupleToRemove);
  return currentVerses.filter(tuple => verseTupleToKey(tuple) !== keyToRemove);
}

export function getPreviewVerses(response, fallbackTuples) {
  const verses = Array.isArray(response) ? response : response?.verses;
  if (!Array.isArray(verses)) {
    return fallbackTuples.map(tuple => ({
      ref: formatVerseTuple(tuple),
      text: 'Preview unavailable',
    }));
  }
  return verses.map((verse, index) => {
    if (typeof verse === 'string') {
      return {
        ref: formatVerseTuple(fallbackTuples[index] || fallbackTuples[0]),
        text: verse,
      };
    }
    return {
      ref: verse.ref || formatVerseTuple(fallbackTuples[index] || fallbackTuples[0]),
      text: verse.text || 'Preview unavailable',
    };
  });
}

export const CreateDeck = () => {
  const { user } = useUserAuth();
  const history = useHistory();
  const viewportHeight = useViewportHeight();
  const initialDraft = useMemo(() => loadDraft(), []);
  const [currentStep, setCurrentStep] = useState(initialDraft?.currentStep || 1);
  const [referenceInput, setReferenceInput] = useState('');
  const [selectedVerses, setSelectedVerses] = useState(initialDraft?.selectedVerses || []);
  const [page, setPage] = useState(0);
  const [tierId, setTierId] = useState(initialDraft?.tierId || 'medium');
  const [paceTarget, setPaceTarget] = useState(initialDraft?.paceTarget || 3);
  const [dueDate, setDueDate] = useState(initialDraft?.dueDate || '');
  const [deckName, setDeckName] = useState(initialDraft?.deckName || '');
  const [isPublic, setIsPublic] = useState(initialDraft?.isPublic || false);
  const [preview, setPreview] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdDeckId, setCreatedDeckId] = useState('');
  const today = getTodayDateString();
  const pageCount = Math.max(1, Math.ceil(selectedVerses.length / PAGE_SIZE));
  const visibleVerses = selectedVerses.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const recommendation = useMemo(() => {
    if (!dueDate || selectedVerses.length === 0 || !isValidDueDate(dueDate, today)) {
      return null;
    }
    return recommendDeckPace(selectedVerses.length, dueDate, today);
  }, [dueDate, selectedVerses.length, today]);

  useEffect(() => {
    saveDraft({
      currentStep,
      selectedVerses,
      tierId,
      paceTarget,
      dueDate,
      deckName,
      isPublic,
    });
  }, [currentStep, selectedVerses, tierId, paceTarget, dueDate, deckName, isPublic]);

  useEffect(() => {
    if (page >= pageCount) {
      setPage(pageCount - 1);
    }
  }, [page, pageCount]);

  useEffect(() => {
    const previewTuples = selectedVerses
      .slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
      .slice(0, PREVIEW_SIZE);
    if (previewTuples.length === 0) {
      setPreview([]);
      setPreviewError('');
      return undefined;
    }

    let active = true;
    setPreviewLoading(true);
    setPreviewError('');
    const references = previewTuples.map(formatVerseTuple).join('; ');
    const language = getBibleLanguageFromCookie() || BIBLE_LANGUAGES.ENGLISH;
    fetchLSMVersesWithRetry(references, 'default', language)
      .then(response => {
        if (active) {
          setPreview(getPreviewVerses(response, previewTuples));
        }
      })
      .catch(() => {
        if (active) {
          setPreview([]);
          setPreviewError('Bible text preview is temporarily unavailable.');
        }
      })
      .finally(() => {
        if (active) {
          setPreviewLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [page, selectedVerses]);

  const handleAddVerses = event => {
    event.preventDefault();
    setError('');
    try {
      const updated = addVerseReferenceInput(selectedVerses, referenceInput);
      const addedCount = updated.length - selectedVerses.length;
      setSelectedVerses(updated);
      setReferenceInput('');
      setPage(Math.max(0, Math.ceil(updated.length / PAGE_SIZE) - 1));
      setFeedback(
        addedCount > 0
          ? `${addedCount} verse${addedCount === 1 ? '' : 's'} added.`
          : 'Those verses are already selected.'
      );
    } catch (inputError) {
      setError(inputError.message);
    }
  };

  const handleDueDateChange = event => {
    const value = event.target.value;
    setDueDate(value);
    setError('');
    if (value && isValidDueDate(value, today) && selectedVerses.length > 0) {
      const nextRecommendation = recommendDeckPace(selectedVerses.length, value, today);
      setTierId(nextRecommendation.tier);
      setPaceTarget(nextRecommendation.target);
    }
  };

  const handleTierChange = nextTierId => {
    if (nextTierId === 'custom') {
      const recommendedTarget = recommendation?.tier === 'custom'
        ? recommendation.target
        : null;
      const currentTarget = Number.isInteger(paceTarget) && paceTarget > 0
        ? paceTarget
        : null;
      setTierId('custom');
      setPaceTarget(recommendedTarget || currentTarget || 1);
      return;
    }
    const tier = getPaceTier(nextTierId);
    const currentTargetIsValid = Number.isInteger(paceTarget)
      && paceTarget >= tier.min_target
      && paceTarget <= tier.max_target;
    const recommendedTargetIsValid = recommendation?.tier === nextTierId
      && recommendation.target >= tier.min_target
      && recommendation.target <= tier.max_target;
    setTierId(nextTierId);
    setPaceTarget(
      currentTargetIsValid
        ? paceTarget
        : recommendedTargetIsValid
          ? recommendation.target
          : tier.default_target
    );
  };

  const handleNext = () => {
    setError('');
    if (currentStep === 1 && selectedVerses.length < 1) {
      setError('Add at least one verse.');
      return;
    }
    if (currentStep === 2 && dueDate && !isValidDueDate(dueDate, today)) {
      setError('Due date cannot be before today.');
      return;
    }
    setCurrentStep(step => Math.min(3, step + 1));
  };

  const handleCreate = async () => {
    const trimmedName = deckName.trim();
    if (!trimmedName) {
      setError('Enter a deck name.');
      return;
    }
    if (!user || selectedVerses.length < 1) {
      setError('A signed-in user and at least one verse are required.');
      return;
    }

    setCreating(true);
    setError('');
    try {
      const pace = createPace(tierId, paceTarget);
      const deckId = await createDeckInDatabase(db, user, {
        deckName: trimmedName,
        isPublic,
        dueDate: dueDate || null,
        pace,
        verses: selectedVerses,
      });
      clearDraft();
      setCreatedDeckId(deckId);
      setFeedback('Deck created successfully.');
    } catch (creationError) {
      setError(creationError.message || 'Unable to create the deck.');
    } finally {
      setCreating(false);
    }
  };

  const paceTier = tierId === 'custom' ? null : getPaceTier(tierId);
  const canContinue = currentStep === 1
    ? selectedVerses.length > 0
    : !dueDate || isValidDueDate(dueDate, today);
  const canCreate = Boolean(deckName.trim() && selectedVerses.length > 0 && !creating);

  return (
    <div style={{
      height: viewportHeight,
      overflow: 'hidden',
      backgroundColor: 'var(--md-sys-color-surface)',
      color: 'var(--md-sys-color-on-surface)',
    }}>
      <MaterialHeader
        variant="create"
        title="Create a Deck"
        onClose={() => history.push('/')}
      />
      <main style={{
        height: `calc(${viewportHeight} - 64px)`,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}>
        {!user ? (
          <div style={{
            flex: 1,
            display: 'grid',
            placeItems: 'center',
            padding: '16px',
          }}>
            <md-elevated-card style={{ padding: '24px', maxWidth: '400px', textAlign: 'center' }}>
              <Icon name="account" size="hero" style={{ color: 'var(--md-sys-color-primary)' }} />
              <h2>Sign In Required</h2>
              <p>Sign in to create and save a memorization deck.</p>
              <button type="button" onClick={logIn} style={primaryButtonStyle}>
                Sign In
              </button>
            </md-elevated-card>
          </div>
        ) : createdDeckId ? (
          <div style={{
            flex: 1,
            display: 'grid',
            placeItems: 'center',
            padding: '16px',
          }}>
            <md-elevated-card style={{ padding: '24px', maxWidth: '400px', textAlign: 'center' }}>
              <Icon name="check" size="hero" style={{ color: 'var(--md-sys-color-primary)' }} />
              <h2>Deck Created</h2>
              <p>Your memorization deck is ready.</p>
              <a
                href={`/deck?id=${createdDeckId}&from=create`}
                style={{ ...primaryButtonStyle, display: 'inline-block', textDecoration: 'none' }}
              >
                View Deck
              </a>
            </md-elevated-card>
          </div>
        ) : (
          <>
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '16px' }}>
              <div style={{ maxWidth: '760px', margin: '0 auto' }}>
                <p style={{
                  margin: '0 0 12px',
                  color: 'var(--md-sys-color-on-surface-variant)',
                }}>
                  Step {currentStep} of 3
                </p>
                {feedback && (
                  <div
                    role="status"
                    aria-live="polite"
                    style={{
                      padding: '12px',
                      marginBottom: '12px',
                      borderRadius: '12px',
                      backgroundColor: 'var(--md-sys-color-primary-container)',
                      color: 'var(--md-sys-color-on-primary-container)',
                    }}
                  >
                    {feedback}
                  </div>
                )}
                {error && (
                  <div
                    role="alert"
                    style={{
                      padding: '12px',
                      marginBottom: '12px',
                      borderRadius: '12px',
                      backgroundColor: 'var(--md-sys-color-error-container)',
                      color: 'var(--md-sys-color-on-error-container)',
                    }}
                  >
                    {error}
                  </div>
                )}

                {currentStep === 1 && (
                  <md-elevated-card style={{ display: 'block', padding: '20px' }}>
                    <h2 style={{ marginTop: 0 }}>Add Verses</h2>
                    <p style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                      Add a verse, range, chapter, or semicolon-separated list.
                    </p>
                    <form onSubmit={handleAddVerses} style={{ display: 'flex', gap: '8px' }}>
                      <label htmlFor="deck-reference-input" style={{ position: 'absolute', left: '-10000px' }}>
                        Bible references
                      </label>
                      <input
                        id="deck-reference-input"
                        type="text"
                        value={referenceInput}
                        placeholder="John 3:16; Gal 4:1-3"
                        aria-label="Bible references"
                        onChange={event => setReferenceInput(event.target.value)}
                        style={fieldStyle}
                      />
                      <button
                        type="submit"
                        {...(!referenceInput.trim() ? { disabled: true } : {})}
                        style={{
                          ...primaryButtonStyle,
                          opacity: referenceInput.trim() ? 1 : 0.55,
                        }}
                      >
                        Add
                      </button>
                    </form>

                    <h3>{selectedVerses.length.toLocaleString()} selected</h3>
                    {visibleVerses.length > 0 && (
                      <>
                        <ul aria-label="Selected verses" style={{ padding: 0, listStyle: 'none' }}>
                          {visibleVerses.map(tuple => (
                            <li
                              key={verseTupleToKey(tuple)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '12px',
                                padding: '8px 0',
                                borderBottom: '1px solid var(--md-sys-color-outline-variant)',
                              }}
                            >
                              <span>{formatVerseTuple(tuple)}</span>
                              <button
                                type="button"
                                aria-label={`Remove ${formatVerseTuple(tuple)}`}
                                onClick={() => {
                                  setSelectedVerses(current => removeSelectedVerse(current, tuple));
                                  setFeedback(`${formatVerseTuple(tuple)} removed.`);
                                }}
                                style={{
                                  border: 0,
                                  background: 'transparent',
                                  color: 'var(--md-sys-color-error)',
                                  padding: '8px',
                                  cursor: 'pointer',
                                }}
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                        {pageCount > 1 && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                            marginTop: '12px',
                          }}>
                            <button
                              type="button"
                              {...(page === 0 ? { disabled: true } : {})}
                              onClick={() => setPage(current => current - 1)}
                              aria-label="Previous selected verses page"
                            >
                              Previous
                            </button>
                            <span>Page {page + 1} of {pageCount}</span>
                            <button
                              type="button"
                              {...(page === pageCount - 1 ? { disabled: true } : {})}
                              onClick={() => setPage(current => current + 1)}
                              aria-label="Next selected verses page"
                            >
                              Next
                            </button>
                          </div>
                        )}
                        <section aria-labelledby="verse-preview-title" style={{ marginTop: '20px' }}>
                          <h3 id="verse-preview-title">Text Preview</h3>
                          {previewLoading && <p role="status">Loading Bible text…</p>}
                          {previewError && <p role="alert">{previewError}</p>}
                          {!previewLoading && (
                            <VersePreviewList verses={preview} />
                          )}
                        </section>
                      </>
                    )}
                  </md-elevated-card>
                )}

                {currentStep === 2 && (
                  <md-elevated-card style={{ display: 'block', padding: '20px' }}>
                    <h2 style={{ marginTop: 0 }}>Choose a Pace</h2>
                    <label htmlFor="deck-due-date" style={{ display: 'block', fontWeight: 500 }}>
                      Due date (optional)
                    </label>
                    <input
                      id="deck-due-date"
                      type="date"
                      min={today}
                      value={dueDate}
                      onChange={handleDueDateChange}
                      style={{ ...fieldStyle, margin: '8px 0 16px' }}
                    />
                    {dueDate && !isValidDueDate(dueDate, today) && (
                      <p role="alert" style={{ color: 'var(--md-sys-color-error)' }}>
                        Due date cannot be before today.
                      </p>
                    )}
                    {recommendation && (
                      <p role="status">
                        Recommended: {recommendation.target} verse
                        {recommendation.target === 1 ? '' : 's'} per day for {recommendation.days} day
                        {recommendation.days === 1 ? '' : 's'} ({recommendation.label}).
                      </p>
                    )}
                    <div
                      role="group"
                      aria-label="Pace tier"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                        gap: '8px',
                      }}
                    >
                      {DECK_PACE_TIERS.map(tier => (
                        <button
                          key={tier.id}
                          type="button"
                          aria-pressed={tierId === tier.id ? 'true' : 'false'}
                          onClick={() => handleTierChange(tier.id)}
                          style={{
                            padding: '12px',
                            borderRadius: '12px',
                            border: `1px solid var(--md-sys-color-${tierId === tier.id ? 'primary' : 'outline'})`,
                            backgroundColor: tierId === tier.id
                              ? 'var(--md-sys-color-primary-container)'
                              : 'var(--md-sys-color-surface-container)',
                            color: 'var(--md-sys-color-on-surface)',
                            cursor: 'pointer',
                          }}
                        >
                          <strong>{tier.label}</strong><br />
                          {tier.minutes} min · {tier.min_target === tier.max_target
                            ? tier.min_target
                            : `${tier.min_target}-${tier.max_target}`} verses
                        </button>
                      ))}
                      <button
                        type="button"
                        aria-pressed={tierId === 'custom' ? 'true' : 'false'}
                        onClick={() => handleTierChange('custom')}
                        style={{
                          padding: '12px',
                          borderRadius: '12px',
                          border: `1px solid var(--md-sys-color-${tierId === 'custom' ? 'primary' : 'outline'})`,
                          backgroundColor: tierId === 'custom'
                            ? 'var(--md-sys-color-primary-container)'
                            : 'var(--md-sys-color-surface-container)',
                          color: 'var(--md-sys-color-on-surface)',
                          cursor: 'pointer',
                        }}
                      >
                        <strong>Custom</strong><br />Any target
                      </button>
                    </div>
                    <label htmlFor="pace-target" style={{ display: 'block', marginTop: '20px', fontWeight: 500 }}>
                      Exact daily target
                    </label>
                    {paceTier ? (
                      <select
                        id="pace-target"
                        value={paceTarget}
                        onChange={event => setPaceTarget(Number(event.target.value))}
                        style={{ ...fieldStyle, marginTop: '8px' }}
                      >
                        {Array.from(
                          { length: paceTier.max_target - paceTier.min_target + 1 },
                          (_, index) => paceTier.min_target + index
                        ).map(target => (
                          <option key={target} value={target}>{target} verses per day</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id="pace-target"
                        type="number"
                        min="1"
                        value={paceTarget}
                        onChange={event => setPaceTarget(
                          Math.max(1, Math.floor(Number(event.target.value) || 1))
                        )}
                        style={{ ...fieldStyle, marginTop: '8px' }}
                      />
                    )}
                  </md-elevated-card>
                )}

                {currentStep === 3 && (
                  <md-elevated-card style={{ display: 'block', padding: '20px' }}>
                    <h2 style={{ marginTop: 0 }}>Name and Review</h2>
                    <label htmlFor="deck-name" style={{ display: 'block', fontWeight: 500 }}>
                      Deck name
                    </label>
                    <input
                      id="deck-name"
                      type="text"
                      value={deckName}
                      onChange={event => setDeckName(event.target.value)}
                      placeholder="My memorization deck"
                      style={{ ...fieldStyle, margin: '8px 0 20px' }}
                    />
                    <fieldset style={{ border: 0, padding: 0, margin: '0 0 20px' }}>
                      <legend style={{ fontWeight: 500, marginBottom: '8px' }}>Visibility</legend>
                      <p style={{
                        margin: '0 0 8px',
                        color: 'var(--md-sys-color-on-surface-variant)',
                      }}>
                        Both private and public decks can be shared by link. Public decks are also listed in discovery.
                      </p>
                      <label style={{ display: 'block', padding: '8px 0' }}>
                        <input
                          type="radio"
                          name="deck-visibility"
                          {...(!isPublic ? { checked: true } : {})}
                          onChange={() => setIsPublic(false)}
                        /> Private
                      </label>
                      <label style={{ display: 'block', padding: '8px 0' }}>
                        <input
                          type="radio"
                          name="deck-visibility"
                          {...(isPublic ? { checked: true } : {})}
                          onChange={() => setIsPublic(true)}
                        /> Public
                      </label>
                    </fieldset>
                    <dl>
                      <dt>Verses</dt>
                      <dd>{selectedVerses.length.toLocaleString()}</dd>
                      <dt>Pace</dt>
                      <dd>{paceTarget} verses per day</dd>
                      <dt>Due date</dt>
                      <dd>{dueDate || 'None'}</dd>
                    </dl>
                  </md-elevated-card>
                )}
              </div>
            </div>

            <nav
              aria-label="Deck creation steps"
              style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                minHeight: '72px',
                padding: '12px 16px',
                borderTop: '1px solid var(--md-sys-color-outline-variant)',
                backgroundColor: 'var(--md-sys-color-surface)',
              }}
            >
              {currentStep > 1 ? (
                <button
                  type="button"
                  onClick={() => {
                    setError('');
                    setCurrentStep(step => step - 1);
                  }}
                  style={{
                    ...primaryButtonStyle,
                    backgroundColor: 'transparent',
                    color: 'var(--md-sys-color-primary)',
                  }}
                >
                  Back
                </button>
              ) : <span />}
              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  {...(!canContinue ? { disabled: true } : {})}
                  style={{ ...primaryButtonStyle, opacity: canContinue ? 1 : 0.55 }}
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleCreate}
                  {...(!canCreate ? { disabled: true } : {})}
                  style={{ ...primaryButtonStyle, opacity: canCreate ? 1 : 0.55 }}
                >
                  {creating ? 'Creating…' : 'Create Deck'}
                </button>
              )}
            </nav>
          </>
        )}
      </main>
    </div>
  );
};
